<div class="page-container">
  <div class="top-spacer"></div>

  @if (isUpdate && (isLoading$ | async)) {
    <div class="mat-elevation-z1 shimmer-container">
      @for (_ of [].constructor(10); track _) {
        <div class="stencil-shimmer shimmer"></div>
      }
    </div>
  }

  @if (!isUpdate || (isLoading$ | async) === false) {
    <mat-expansion-panel class="settings-card" expanded="true">
      <mat-expansion-panel-header class="expansion-header">
        <div class="title-description">
          <mat-panel-title class="settings-card-title">
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP.TITLE' | translate }}
          </mat-panel-title>
          <mat-panel-description class="settings-card-description">
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP.DESCRIPTION' | translate }}
          </mat-panel-description>
        </div>
      </mat-expansion-panel-header>
      <form [formGroup]="form">
        <glxy-form-field>
          <glxy-label>
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.ABOUT_SECTION.NAME' | translate }}
          </glxy-label>
          <input matInput required formControlName="name" />
          @if (form?.get('name').invalid && (form?.get('name').touched || form?.get('name').dirty)) {
            <glxy-error>
              {{ 'MEETING_SCHEDULER.BOOKING_LINK.NAME_VALIDATION_REQUIRED' | translate }}
            </glxy-error>
          }
        </glxy-form-field>
        <glxy-form-field [prefixText]="linkPrefix$ | async">
          <glxy-label>
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.ABOUT_SECTION.LINK_LABEL' | translate }}
          </glxy-label>
          <input matInput required formControlName="slug" />
          @if (slugChanged) {
            <button mat-icon-button matSuffix (click)="revertSlug()">
              <mat-icon>undo</mat-icon>
            </button>
          }
          @if (slugFormControl.invalid && (slugFormControl.touched || slugFormControl.dirty)) {
            <glxy-error>
              @if (slugFormControl.hasError('required')) {
                {{ 'MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_REQUIRED' | translate }}
              } @else {
                @if (slugFormControl.hasError('pattern')) {
                  {{ 'MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_PATTERN' | translate }}
                } @else {
                  @if (slugFormControl.hasError('unique')) {
                    {{ 'MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_UNIQUE' | translate }}
                  } @else {
                    {{ 'MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_UNKNOWN' | translate }}
                  }
                }
              }
            </glxy-error>
          }
        </glxy-form-field>
        <glxy-form-field appearance="outline" class="fullwidth">
          <glxy-label>
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.ABOUT_SECTION.DESCRIPTION' | translate }}
          </glxy-label>
          <textarea matAutosizeMinRows="4" matInput matTextareaAutosize formControlName="description"></textarea>
        </glxy-form-field>
        <glxy-form-field>
          <div class="color-label">
            <glxy-label>
              {{ 'MEETING_SCHEDULER.BOOKING_LINK.ABOUT_SECTION.COLOR_LABEL' | translate }}
            </glxy-label>
          </div>
        </glxy-form-field>
        <div class="color-container">
          @for (color of colors; track color) {
            <button
              mat-mini-fab
              class="color-selector"
              [ngStyle]="{ 'background-color': color }"
              (click)="setColor(color)"
            >
              @if (color === form.get('color').value) {
                <mat-icon>check</mat-icon>
              }
            </button>
          }
        </div>
      </form>
    </mat-expansion-panel>
  }

  @if (!isUpdate || (isLoading$ | async) === false) {
    <mat-expansion-panel class="settings-card" expanded="true">
      <mat-expansion-panel-header class="expansion-header">
        <div class="title-description">
          <mat-panel-title class="settings-card-title">
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP_EVENT_TYPE.TITLE' | translate }}
          </mat-panel-title>
          <mat-panel-description>
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP_EVENT_TYPE.DESCRIPTION' | translate }}
          </mat-panel-description>
        </div>
      </mat-expansion-panel-header>
      <form [formGroup]="events">
        <glxy-form-field>
          <glxy-label>
            {{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP_EVENT_TYPE.SELECT' | translate }}
          </glxy-label>
          <mat-select
            formControlName="eventList"
            placeholder="{{ 'MEETING_SCHEDULER.BOOKING_LINK.GROUP_EVENT_TYPE.SELECT_PLACEHOLDER' | translate }}"
            (selectionChange)="onSelectionChange($event)"
            multiple
          >
            <mat-select-trigger>
              {{ eventList.value?.[0]?.name || '' }}
              @if ((eventList.value?.length || 0) > 1) {
                <span class="example-additional-selection">
                  (+{{ (eventList.value?.length || 0) - 1 }} {{ eventList.value?.length === 2 ? 'other' : 'others' }})
                </span>
              }
            </mat-select-trigger>
            <!-- Personal Events -->
            @if (personalEventTypes.length) {
              <mat-optgroup
                class="event-type-optgroup"
                label="{{ 'MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENTS' | translate }}"
              >
                @for (option of personalEventTypes; track option) {
                  <mat-option [value]="option">
                    {{ option.name }}
                  </mat-option>
                }
              </mat-optgroup>
            }
            <!-- Team Events -->
            @if (teamEventTypes.length) {
              <mat-optgroup
                class="event-type-optgroup"
                label="{{ 'MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENTS' | translate }}"
              >
                @for (option of teamEventTypes; track option) {
                  <mat-option [value]="option">
                    {{ option.name }}
                  </mat-option>
                }
              </mat-optgroup>
            }
          </mat-select>
          @if (isEventTypeNotSelected && selectedOptions?.length < 2 && (events?.touched || events?.dirty)) {
            <glxy-error>
              {{ 'MEETING_SCHEDULER.BOOKING_LINK.EVENT_TYPE_VALIDATION_REQUIRED' | translate }}
            </glxy-error>
          }
        </glxy-form-field>
        <div class="dynamic-inputs" cdkDropList (cdkDropListDropped)="onDrop($event)">
          @for (selected of selectedOptions; track selected.id; let i = $index) {
            <div class="dynamic-item" cdkDrag [cdkDragData]="selected">
              <mat-icon class="drag-icon" aria-hidden="true">drag_indicator</mat-icon>
              <glxy-form-field appearance="outline" class="input-field">
                <div class="input-badge-container">
                  <input matInput [value]="selected.name" readonly />
                  @if (selected.creator) {
                    <mat-icon [glxyTooltip]="selected.creator" class="info-icon" aria-hidden="true"
                      >info_outline</mat-icon
                    >
                  }
                  @if (selected.name) {
                    <glxy-badge [color]="getBadgeColor(selected.type)">
                      {{ selected.type | eventType }}
                    </glxy-badge>
                  }
                </div>
              </glxy-form-field>
              <mat-icon class="delete-icon" aria-hidden="true" (click)="onDelete(i)"> delete_outline </mat-icon>
            </div>
          }
        </div>
      </form>
    </mat-expansion-panel>
  }

  @if (isUpdate && (isLoading$ | async) === false) {
    <mat-expansion-panel class="settings-card day-card" expanded="false">
      <mat-expansion-panel-header class="expansion-header">
        <div class="title-description">
          <mat-panel-title class="settings-card-title">
            {{ 'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ADD_TO_WEBSITE.TITLE' | translate }}
          </mat-panel-title>
          <mat-panel-description class="Availability-card-description">
            {{ 'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ADD_TO_WEBSITE.PROMPT' | translate }}
          </mat-panel-description>
        </div>
      </mat-expansion-panel-header>
      @if (originalSlug !== undefined && originalSlug !== null) {
        <div>
          <meeting-scheduler-settings-add-to-website-card
            [isUpdate]="isUpdate"
            [type]="'group'"
            [id]="groupId"
            [slug]="originalSlug"
            [name]="groupName"
          ></meeting-scheduler-settings-add-to-website-card>
        </div>
      }
    </mat-expansion-panel>
  }

  @if ((isLoading$ | async) === false) {
    <glxy-sticky-footer [rightAligned]="true">
      <button
        mat-flat-button
        color="primary"
        type="submit"
        (click)="createOrUpdateGroup()"
        [disabled]="isButtonLoading$ | async"
      >
        <glxy-button-loading-indicator [isLoading]="isButtonLoading$ | async">{{
          buttonText
        }}</glxy-button-loading-indicator>
      </button>
    </glxy-sticky-footer>
  }
</div>
